import React from 'react'
import { Show } from '@refinedev/antd'
import { useShow, useList } from '@refinedev/core'
import { Card, Col, Divider, Empty, Row, Space, Tag, Timeline, Typography } from 'antd'
import type { Tables } from '@muaa/shared'
import { ModerationActions } from './ModerationActions'
import { brand, initials, statusColor } from '../../theme/brand'

type Profile = Tables<'profiles'>

interface AnalyticsEvent { id: string; event_name: string; screen: string | null; occurred_at: string }
interface UserStat { events: number; screens: number; sessions: number; first_seen: string | null; last_seen: string | null; last_screen: string | null }

/** Human-readable description of an analytics event for the journey timeline. */
function describeEvent(e: AnalyticsEvent): string {
  if (e.event_name === 'screen_view') return `Viewed ${e.screen ?? 'a screen'}`
  const label = e.event_name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  return e.screen ? `${label} · ${e.screen}` : label
}

const { Paragraph, Text } = Typography

/** Status-colored role pill (member/admin → palette). */
function rolePill(role?: string | null): React.ReactNode {
  if (!role) return <Text type="secondary">—</Text>
  const [fg, bg] = statusColor(role)
  return (
    <span
      style={{
        fontFamily: brand.fSans,
        fontWeight: 700,
        fontSize: 12,
        color: fg,
        background: bg,
        padding: '3px 12px',
        borderRadius: 999,
        textTransform: 'capitalize',
      }}
    >
      {role}
    </span>
  )
}

const headStyle: React.CSSProperties = { fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }

/** Small gold uppercase section eyebrow. */
const sectionLabel: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 8,
}

/** A labelled key/value line for scalar attributes (Professional / Contact). */
function Field({
  label,
  value,
  copyable,
}: {
  label: string
  value?: string | number | null
  copyable?: boolean
}) {
  const has = value !== null && value !== undefined && value !== ''
  return (
    <div style={{ marginBottom: 11 }}>
      <div style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginBottom: 2 }}>{label}</div>
      {has ? (
        <Typography.Text
          copyable={copyable}
          style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13.5, color: brand.ink }}
        >
          {String(value)}
        </Typography.Text>
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      )}
    </div>
  )
}

export function ProfileShow() {
  const { query } = useShow<Profile>()
  const { data, isLoading } = query
  const record = data?.data
  const userId = record?.user_id ?? undefined

  // Product-analytics journey for this member (admin-gated views).
  const { result: journeyRes } = useList<AnalyticsEvent>({
    resource: 'analytics_events',
    filters: userId ? [{ field: 'user_id', operator: 'eq', value: userId }] : [],
    sorters: [{ field: 'occurred_at', order: 'desc' }],
    pagination: { pageSize: 30 },
    queryOptions: { enabled: !!userId },
  })
  const { result: usageRes } = useList<UserStat>({
    resource: 'analytics_user_stats',
    filters: userId ? [{ field: 'user_id', operator: 'eq', value: userId }] : [],
    pagination: { pageSize: 1 },
    queryOptions: { enabled: !!userId },
  })
  const journey = journeyRes?.data ?? []
  const usage = usageRes?.data?.[0]

  const name = record?.name ?? ''
  const joined = record?.created_at
    ? new Date(record.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—'

  return (
    <Show isLoading={isLoading}>
      {/* ── Hero header: avatar + identity + key facts (full width, navy/gold) ── */}
      <div
        style={{
          background: brand.navyGrad,
          borderRadius: 16,
          padding: '24px 28px',
          marginBottom: 16,
          border: `1px solid ${brand.navy700}`,
        }}
      >
        <Row align="middle" gutter={[24, 16]} wrap>
          <Col flex="none">
            <div
              style={{
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: brand.jaguar,
                color: brand.paper,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: brand.fDisplay,
                fontWeight: 700,
                fontSize: 30,
                border: `2px solid ${brand.gold}`,
                boxShadow: '0 2px 12px rgba(0,0,0,.28)',
              }}
            >
              {initials(name) || '—'}
            </div>
          </Col>

          <Col flex="auto" style={{ minWidth: 220 }}>
            <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 26, color: '#fff', lineHeight: 1.15 }}>
              {name || 'Unnamed member'}
            </div>
            <div style={{ fontFamily: brand.fSans, fontSize: 13.5, color: 'rgba(255,255,255,.72)', marginTop: 4 }}>
              {record?.email ?? 'No email on file'}
            </div>
            <Space size={8} wrap style={{ marginTop: 12 }}>
              {record?.class_year ? (
                <span
                  style={{
                    fontFamily: brand.fDisplay,
                    fontWeight: 700,
                    fontSize: 12,
                    color: brand.navy900,
                    background: brand.goldGrad,
                    padding: '3px 12px',
                    borderRadius: 999,
                  }}
                >
                  Class of {record.class_year}
                </span>
              ) : null}
              {rolePill(record?.role)}
              {record?.city ? (
                <span
                  style={{
                    fontFamily: brand.fSans,
                    fontWeight: 600,
                    fontSize: 12,
                    color: 'rgba(255,255,255,.88)',
                    background: 'rgba(255,255,255,.10)',
                    border: '1px solid rgba(255,255,255,.16)',
                    padding: '3px 12px',
                    borderRadius: 999,
                  }}
                >
                  {record.city}
                </span>
              ) : null}
            </Space>
          </Col>

          <Col flex="none">
            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  fontFamily: brand.fSans,
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  color: 'rgba(255,255,255,.5)',
                }}
              >
                Joined
              </div>
              <div style={{ fontFamily: brand.fSans, fontWeight: 700, fontSize: 15, color: brand.gold3, marginTop: 3 }}>
                {joined}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      {/* ── Profile details: each attribute in a format that fits its type — ──
          prose for the bio, chips for tags, key/values for professional + contact.
          Class year / city / role live in the hero above, so they aren't repeated. */}
      <Card title={<span style={headStyle}>Profile details</span>} style={{ marginBottom: 16, borderColor: brand.cardLine }}>
        <Row gutter={[28, 20]}>
          {/* Narrative: About + Interests */}
          <Col xs={24} lg={15}>
            <div style={sectionLabel}>About</div>
            {record?.bio ? (
              <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', color: brand.ink, fontSize: 14, lineHeight: 1.7 }}>
                {record.bio}
              </Paragraph>
            ) : (
              <Text type="secondary">No bio provided yet.</Text>
            )}

            <div style={{ ...sectionLabel, marginTop: 22 }}>Interests &amp; Tags</div>
            {record?.tags?.length ? (
              <Space size={[8, 8]} wrap>
                {record.tags.map((t: string) => (
                  <Tag key={t} color="gold" style={{ margin: 0, borderRadius: 999, padding: '2px 12px', fontWeight: 600 }}>
                    {t}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">No tags added.</Text>
            )}
          </Col>

          {/* Key facts: Professional + Contact */}
          <Col xs={24} lg={9}>
            <div
              style={{
                background: brand.paper,
                border: `1px solid ${brand.cardLine}`,
                borderRadius: 12,
                padding: '16px 18px',
              }}
            >
              <div style={sectionLabel}>Professional</div>
              <Field label="Title" value={record?.title} />
              <Field label="Organization" value={record?.org} />

              <Divider style={{ margin: '14px 0' }} />

              <div style={sectionLabel}>Contact</div>
              <Field label="Email" value={record?.email} copyable />
            </div>
          </Col>
        </Row>
      </Card>

      {/* ── Location ── */}
      <Card title={<span style={headStyle}>Location</span>} style={{ marginBottom: 16, borderColor: brand.cardLine }}>
        <Row gutter={[28, 16]}>
          <Col xs={24} sm={8}>
            <Field label="City" value={record?.city} />
          </Col>
          <Col xs={12} sm={4}>
            <Field label="State / Region" value={record?.state} />
          </Col>
          <Col xs={12} sm={4}>
            <Field label="Country" value={record?.country} />
          </Col>
          <Col xs={12} sm={4}>
            <div style={{ marginBottom: 11 }}>
              <div style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginBottom: 2 }}>Latitude</div>
              <Typography.Text style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13.5, color: brand.ink }}>
                {record?.latitude != null ? record.latitude : <Typography.Text type="secondary">Auto from city</Typography.Text>}
              </Typography.Text>
            </div>
          </Col>
          <Col xs={12} sm={4}>
            <div style={{ marginBottom: 11 }}>
              <div style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, marginBottom: 2 }}>Longitude</div>
              <Typography.Text style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13.5, color: brand.ink }}>
                {record?.longitude != null ? record.longitude : <Typography.Text type="secondary">Auto from city</Typography.Text>}
              </Typography.Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* ── Activity & journey (product analytics) ── */}
      <Card title={<span style={headStyle}>Activity &amp; journey</span>} style={{ marginBottom: 16, borderColor: brand.cardLine }}>
        {usage ? (
          <Row gutter={16} style={{ marginBottom: journey.length > 0 ? 18 : 0 }}>
            {([
              ['Events', usage.events],
              ['Screens', usage.screens],
              ['Sessions', usage.sessions],
              ['Last active', usage.last_seen ? new Date(usage.last_seen).toLocaleDateString() : '—'],
            ] as [string, number | string][]).map(([label, value]) => (
              <Col key={label} xs={12} sm={6}>
                <div style={{ fontFamily: brand.fDisplay, fontWeight: 800, fontSize: 22, color: brand.ink }}>
                  {typeof value === 'number' ? value.toLocaleString() : value}
                </div>
                <div style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>{label}</div>
              </Col>
            ))}
          </Row>
        ) : null}
        {journey.length > 0 ? (
          <Timeline
            items={journey.map((e) => ({
              color: e.event_name === 'screen_view' ? brand.jaguar : '#1f7a4d',
              children: (
                <div>
                  <span style={{ fontFamily: brand.fSans, fontWeight: 600, fontSize: 13, color: brand.ink }}>
                    {describeEvent(e)}
                  </span>
                  <div style={{ fontFamily: brand.fSans, fontSize: 11.5, color: brand.sub }}>
                    {new Date(e.occurred_at).toLocaleString()}
                  </div>
                </div>
              ),
            }))}
          />
        ) : (
          <Empty description="No activity recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Card>

      {/* ── Integrity actions ── */}
      <Card title={<span style={headStyle}>Integrity actions</span>} style={{ borderColor: brand.cardLine }}>
        <ModerationActions userId={record?.user_id ?? undefined} />
      </Card>
    </Show>
  )
}
